import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "./lib/utils";
import { Button } from "./components/ui/button";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";

/**
 * Single-date picker.
 * - User can pick any date (no constraint to "what data exists").
 * - Calling `onChange` only happens when a date is actually selected.
 */
export function DatePicker({ value, onChange, placeholder = "Select date", disabled, "data-testid": testId, className, fromDate, toDate }) {
  const [open, setOpen] = React.useState(false);
  const date = value ? new Date(value + "T00:00:00") : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "h-8 text-xs justify-start font-normal w-full",
            !date && "text-muted-foreground",
            className,
          )}
          data-testid={testId}
        >
          <CalendarIcon className="w-3 h-3 mr-1" />
          {date ? format(date, "PPP") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => {
            if (d) {
              onChange(format(d, "yyyy-MM-dd"));
              setOpen(false);
            }
          }}
          fromDate={fromDate}
          toDate={toDate}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Multi-date picker — user selects N discrete dates from the calendar.
 * value is an array of "YYYY-MM-DD" strings.
 *
 * Used by Roster Analysis where the user wants to compare specific days
 * (e.g. last Monday + last Tuesday) rather than a contiguous range.
 */
export function MultiDatePicker({ value = [], onChange, placeholder = "Select dates", disabled, "data-testid": testId, className, fromDate, toDate }) {
  const [open, setOpen] = React.useState(false);
  const dates = value.map((s) => new Date(s + "T00:00:00"));
  const label = value.length === 0
    ? placeholder
    : value.length === 1
    ? format(dates[0], "PPP")
    : `${value.length} dates`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "h-8 text-xs justify-start font-normal w-full",
            value.length === 0 && "text-muted-foreground",
            className,
          )}
          data-testid={testId}
        >
          <CalendarIcon className="w-3 h-3 mr-1" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="start">
        <Calendar
          mode="multiple"
          selected={dates}
          onSelect={(ds) => {
            const formatted = (ds || []).map((d) => format(d, "yyyy-MM-dd")).sort();
            onChange(formatted);
          }}
          fromDate={fromDate}
          toDate={toDate}
          initialFocus
        />
        <div className="flex justify-between border-t pt-2 mt-2 text-xs">
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-slate-600 hover:text-slate-900"
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-blue-600 font-medium hover:text-blue-800"
            data-testid={testId ? `${testId}-done` : undefined}
          >
            Done
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
